import { useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import toast from "react-hot-toast";
import { Squares2X2Icon } from "@heroicons/react/24/outline";
import { useAccessControl } from "@/hooks/useAccessControl";
import { getWidgetById } from "@/utils/widgets";
import { onboardingTemplates } from "./templates";
import { buildLayoutExportData } from "./templates/packLayout";
import { applyLayoutData } from "./templates/applyTemplate";
import { trackFeatureUsage } from "@/utils/gtm";
import type { OnboardingTemplate } from "./templates/types";

interface TemplateGalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function baseIdOf(id: string): string {
  if (getWidgetById(id)) return id;
  const dashIdx = id.indexOf("-");
  return dashIdx === -1 ? id : id.substring(0, dashIdx);
}

/**
 * Starter-template picker. Reused by the empty tab state, the new-tab modal
 * and the onboarding welcome flow. Applying a template creates a new tab via
 * the standard applyLayoutData pipeline.
 */
export default function TemplateGalleryModal({
  isOpen,
  onClose,
}: TemplateGalleryModalProps) {
  const { hasAccessToWidget } = useAccessControl();
  const [applyingId, setApplyingId] = useState<string | null>(null);

  const widgetNames = (template: OnboardingTemplate): string[] => {
    const seen = new Set<string>();
    const names: string[] = [];
    for (const spec of template.widgets) {
      const widget = getWidgetById(baseIdOf(spec.id));
      if (!widget) continue;
      const label = widget.displayName ?? widget.name;
      if (!seen.has(label)) {
        seen.add(label);
        names.push(label);
      }
    }
    return names;
  };

  const handleApply = (template: OnboardingTemplate) => {
    setApplyingId(template.id);
    try {
      const data = buildLayoutExportData(template.name, template.widgets);
      applyLayoutData(data, { hasAccessToWidget, repackOnDrop: true });
      // A tab created from a template, alongside NewTabModal's "blank" — not
      // an onboarding event. This gallery is open to everyone, any time, so
      // reporting it as onboarding inflated the setup completion count.
      trackFeatureUsage({
        feature_name: "dashboard_tab",
        feature_action: "create",
        feature_detail: template.id,
      });
      toast.success(`"${template.name}" tab created`);
      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to apply template"
      );
    } finally {
      setApplyingId(null);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="relative z-[100] no-drag"
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-900/80 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150"
      />
      <div className="fixed inset-0 z-10 flex items-center justify-center p-4">
        <DialogPanel
          transition
          className="w-full max-w-2xl max-h-[85vh] overflow-y-auto transform rounded-lg bg-gray-800 shadow-xl transition-all data-[closed]:translate-y-2 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[closed]:scale-95"
        >
          <div className="p-5">
            <div className="flex items-center gap-3">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-squawk-blue/20">
                <Squares2X2Icon className="size-5 text-squawk-blue" />
              </div>
              <div>
                <DialogTitle className="text-sm font-semibold text-gray-100">
                  Start from a template
                </DialogTitle>
                <p className="text-sm text-gray-400">
                  Pre-built dashboards for how you trade. You can rearrange
                  everything afterwards.
                </p>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {onboardingTemplates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  disabled={applyingId !== null}
                  onClick={() => handleApply(template)}
                  className="text-left rounded-lg border border-gray-700 bg-gray-900/50 p-4 hover:border-squawk-blue/60 hover:bg-gray-900 transition-colors disabled:opacity-60 cursor-pointer"
                >
                  <div className="text-sm font-semibold text-gray-100">
                    {template.name}
                  </div>
                  <p className="mt-1 text-xs text-gray-400 leading-relaxed">
                    {template.description}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {widgetNames(template).map((name) => (
                      <span
                        key={name}
                        className="rounded bg-gray-700/60 px-1.5 py-0.5 text-[10px] font-medium text-gray-300"
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium rounded-md bg-gray-700/50 border border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-gray-100 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
